import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence for render-time crashes.
 *
 * There was no boundary anywhere in the tree, so a single unexpected payload
 * shape (a capability whose `ui_schema` came back as `{}`, a `params.prompt`
 * that was not a string) unmounted the entire app to a blank white page with
 * nothing in the UI to explain it or recover from it.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled render error", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-studio-border bg-studio-panel p-6 text-center">
          <h1 className="font-display text-lg font-semibold text-studio-ink">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-studio-muted">
            The page hit an unexpected error. Your work in progress may still be
            recoverable by going back.
          </p>
          <p className="mt-3 break-words rounded-lg bg-studio-bg px-3 py-2 text-left font-mono text-xs text-studio-faint">
            {error.message}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-lg border border-studio-border px-3 py-1.5 text-sm font-medium text-studio-ink hover:bg-studio-bg"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-studio-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
